import React from 'react'
import PropTypes from 'prop-types'
import _cx from 'classnames/bind'
import { get } from 'lodash'
import { Text, Table, Grid, Icon } from '@adc/ui-adc'
import { formatMoneyWithNumeral, formatNumber } from '../../utils/formatters'
import { buildRow, DataNotAvailable, privateView } from './utils'
import cls from './index.scss'
const cx = _cx.bind(cls)

const elmId = 'neighborhood'

const badgeColorClass = rating => {
  let color
  if (rating <= 2) color = 'low'
  else if (rating <= 4) color = 'med-low'
  else if (rating <= 6) color = 'medium'
  else if (rating <= 8) color = 'med-high'
  else if (rating <= 10) color = 'high'
  return `${color}-badge`
}

const RatingBadge = ({ rating }) => (
  <Text size='large' color='white' weight='heavy'>
    <span className={cx('rating-badge', badgeColorClass(rating))}>
      {rating}
    </span>{' '}
  </Text>
)

RatingBadge.propTypes = {
  rating: PropTypes.number,
}

const CommunityHighlight = ({ title, icon, highlights }) => (
  <div>
    <Text className={cx('u-mb-3')} size='large' color='gray'>
      <Icon size='big' color='gray' iconStyle='light' name={icon} /> {title}
    </Text>
    {highlights.map(highlight => (
      <div className={cx('highlight')} key={highlight.feature}>
        <Text color='gray' size='tiny'>
          {highlight.feature}
        </Text>
        <Text color='gray' size='tiny' weight='heavy'>
          {parseFloat(highlight.distance).toFixed(2)} mi
        </Text>
      </div>
    ))}
  </div>
)

CommunityHighlight.propTypes = {
  title: PropTypes.string,
  icon: PropTypes.string,
  highlights: PropTypes.array,
}

const FIELDS = [
  {
    label: 'Rating',
    fieldPath: 'additionalinfo[0].value2',
    format: rating => <RatingBadge rating={rating} />,
  },
  {
    label: 'School',
    fieldPath: 'schoolname',
  },
  {
    label: 'Grade',
    fieldPath: 'gradespan',
    format: ({ low, high }) => `${low || ''} - ${high || ''}`,
  },
  {
    label: 'Distance',
    fieldPath: 'distancefromsubj',
    format: distance => `${distance} mi`,
  },
  {
    label: 'Number of Students',
    fieldPath: 'totalenrolled',
    format: formatNumber,
  },
  {
    label: 'Students-Teacher Ratio',
    fieldPath: 'studentteacherratio',
  },
]

const NeighborhoodData = ({ label, value }) => (
  <>
    <Text color='gray' size='tiny'>
      {label}
    </Text>
    <Text className='u-mb-2' color='gray' size='tiny' weight='heavy'>
      {value}
    </Text>
  </>
)

NeighborhoodData.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
}

const ValueTrend = ({ valueTrend }) => {
  const hasValueTrend = Number.isFinite(+valueTrend)
  const positiveTrend = valueTrend >= 0
  let text
  let color
  if (hasValueTrend) {
    text = `${Math.round(valueTrend * 100)}%`
    color = positiveTrend ? 'adc-light-green' : 'caution'
  } else {
    text = 'N/A'
  }

  return (
    <Text size='massive' color={color}>
      {text}
      {valueTrend && (
        <Icon
          color={color}
          iconStyle='regular'
          name={positiveTrend ? 'arrow-circle-up' : 'arrow-circle-down'}
        />
      )}
    </Text>
  )
}

ValueTrend.propTypes = {
  valueTrend: PropTypes.number.isRequired,
}

const GOING_OUT = 'goingOut'
const TRAVEL = 'travel'
const HEALTH = 'health'
const STORES = 'stores'
const OUTDOORS = 'outdoors'

const highlightsMapping = {
  Downtown: GOING_OUT,
  Museum: GOING_OUT,
  Starbucks: GOING_OUT,
  Airport: TRAVEL,
  'Major Hospital': HEALTH,
  Pharmacy: HEALTH,
  Costco: STORES,
  Shopping: STORES,
  'District Park': OUTDOORS,
  Park: OUTDOORS,
}

class Neighborhood extends React.Component {
  get show() {
    return this.props.schools.length > 0 || this.props.highlights.length > 0
  }

  divideHighlights() {
    return this.props.highlights.reduce(
      (memo, { distance, feature }) => {
        if (feature === 'Hospital') {
          memo[HEALTH].push({ feature: 'Local Hospital', distance })
        } else if (feature === "Farmer's Market") {
          memo[STORES].push({ feature: 'Market', distance })
        } else if (feature === 'Public Transport') {
          memo[TRAVEL].push({ feature: 'Public Trans.', distance })
        } else if (feature === 'Rail or Bus Station') {
          memo[TRAVEL].push({ feature: 'Rail/Bus Station', distance })
        } else if (feature === 'EV Charging Station') {
          memo[OUTDOORS].push({ feature: 'EV Charging', distance })
        } else if (memo[highlightsMapping[feature]]) {
          memo[highlightsMapping[feature]].push({ feature, distance })
        }
        return memo
      },
      {
        [GOING_OUT]: [],
        [TRAVEL]: [],
        [HEALTH]: [],
        [STORES]: [],
        [OUTDOORS]: [],
      }
    )
  }

  render() {
    const { loading, neighborhood, schools } = this.props
    const highlights = this.divideHighlights()
    const showZipInfo =
      neighborhood.mediansoldprice ||
      neighborhood.valuetrend ||
      neighborhood.neighborhoodpricerange ||
      neighborhood.medianage ||
      neighborhood.owneroccupancyratio ||
      neighborhood.rentaloccupancyratio
    if (loading) return null

    return !this.show ? (
      <DataNotAvailable />
    ) : (
      <>
        <Text size='large' weight='heavy'>
          Neighborhood Analysis
        </Text>
        {showZipInfo && (
          <>
            <Text className={cx('u-mt-4')} size='large'>
              Zip code {neighborhood.location} sales for last 3 months
            </Text>
            <Grid stackable columns={4} centered padded>
              <Grid.Row>
                <Grid.Column>
                  <Text color='gray' size='tiny'>
                    Median Value
                  </Text>
                  <Text size='massive' weight='heavy'>
                    {formatMoneyWithNumeral(neighborhood.mediansoldprice)}
                  </Text>
                </Grid.Column>
                <Grid.Column>
                  <Text color='gray' size='tiny'>
                    Value Trend
                  </Text>
                  <ValueTrend valueTrend={neighborhood.valuetrend} />
                </Grid.Column>
                <Grid.Column>
                  <NeighborhoodData
                    label='Price Range 2 Years'
                    value={`${formatMoneyWithNumeral(
                      get(neighborhood, 'neighborhoodpricerange.low', 0)
                    )} to ${formatMoneyWithNumeral(
                      get(neighborhood, 'neighborhoodpricerange.high', 0)
                    )}`}
                  />
                  <NeighborhoodData
                    label='Median Property Age'
                    value={`${neighborhood.medianage} Years`}
                  />
                </Grid.Column>
                <Grid.Column>
                  <NeighborhoodData
                    label='Households Occupied By Owners'
                    value={`${Math.round(
                      neighborhood.owneroccupancyratio * 100
                    )}%`}
                  />
                  <NeighborhoodData
                    label='Households Occupied By Renters'
                    value={`${Math.round(
                      neighborhood.rentaloccupancyratio * 100
                    )}%`}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </>
        )}

        {this.props.schools.length > 0 && (
          <>
            <Text className={cx('u-mb-2')} size='large'>
              Schools in the area
            </Text>
            <div className={cx('table')}>
              <Table basic='very' unstackable>
                <Table.Header>
                  <Table.Row>
                    {FIELDS.map(field => (
                      <Table.HeaderCell key={field.label}>
                        {field.label}
                      </Table.HeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {schools.map((comp, i) =>
                    buildRow({
                      comp,
                      elmId,
                      fields: FIELDS,
                      key: i,
                    })
                  )}
                </Table.Body>
              </Table>
            </div>
          </>
        )}
        {this.props.highlights.length > 0 && (
          <>
            <Text className={cx('u-mt-7', 'u-mb-5')} size='large'>
              Community highlights
            </Text>
            <Grid stackable columns={5} centered>
              <Grid.Row>
                <Grid.Column>
                  <CommunityHighlight
                    title='Going Out'
                    icon='city'
                    highlights={highlights[GOING_OUT]}
                  />
                </Grid.Column>
                <Grid.Column>
                  <CommunityHighlight
                    title='Travel'
                    icon='map-marked'
                    highlights={highlights[TRAVEL]}
                  />
                </Grid.Column>
                <Grid.Column>
                  <CommunityHighlight
                    title='Health'
                    icon='first-aid'
                    highlights={highlights[HEALTH]}
                  />
                </Grid.Column>
                <Grid.Column>
                  <CommunityHighlight
                    title='Stores'
                    icon='shopping-cart'
                    highlights={highlights[STORES]}
                  />
                </Grid.Column>
                <Grid.Column>
                  <CommunityHighlight
                    title='Outdoors'
                    icon='tree-alt'
                    highlights={highlights[OUTDOORS]}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </>
        )}
      </>
    )
  }
}

Neighborhood.propTypes = {
  neighborhood: PropTypes.object,
  schools: PropTypes.array,
  highlights: PropTypes.array,
  loading: PropTypes.bool,
}

export default privateView(Neighborhood)
